import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Typed from "typed.js";
import { useTranslation } from "react-i18next";

import { ROUTE } from "@/route/app_routes.js";
import { getPlatformStats } from "@/services/courseService";

const SLIDE_COUNT = 2;
const AUTO_ADVANCE_MS = 9000;

// Owner-specified display rule for the hero's course/student/instructor counters: show
// (real count - 1) with a trailing "+" -- e.g. 6 real courses displays as "5+", 12 real
// instructors as "11+". Only ever applied to a real count the API has returned, never to
// the static pre-load placeholder copy below (which was never a real count to begin with).
const formatStatCount = (n) => `${Math.max(0, (n ?? 0) - 1)}+`;

export const Banner = () => {
  const { t, i18n } = useTranslation("home");
  const [stats, setStats] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const typedRef = useRef(null);
  const titleLineRef = useRef(null);
  const slideRefs = useRef([]);

  // React 18 doesn't pass the `inert` boolean prop through to the DOM (only added to its
  // known-HTML-attributes allowlist in React 19), so JSX inert={...} on the slide divs below
  // is silently dropped. Setting the real DOM property imperatively here is what actually
  // keeps the inactive slide's CTA links out of tab order (aria-hidden alone hides it from
  // screen readers but doesn't stop keyboard focus from landing on an invisible link
  // mid-crossfade).
  useEffect(() => {
    slideRefs.current.forEach((el, i) => {
      if (el) el.inert = i !== activeSlide;
    });
  }, [activeSlide]);

  useEffect(() => {
    getPlatformStats()
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const typed = new Typed(typedRef.current, {
      strings: [
        t("hero.typed.code", "to code."),
        t("hero.typed.gameDev", "game development."),
        t("hero.typed.modeling3d", "3D modeling."),
        t("hero.typed.graphicsDesign", "graphics design."),
        t("hero.typed.productDesign", "product design."),
      ],
      typeSpeed: 90,
      backSpeed: 60,
      backDelay: 2500,
      startDelay: 700,
      loop: true,
      showCursor: true,
      cursorChar: "|",
    });
    return () => typed.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // The typed word's length swings a lot ("code." vs. "graphics design."),
  // but the line's font-size is fixed at a reasonably large size -- if a
  // long word doesn't fit, growing the box (or wrapping to a 2nd line)
  // would move the whole hero's height every few seconds. Instead this
  // shrinks JUST this line's font-size, only when the current word would
  // actually overflow, back up to the full size the rest of the time --
  // so most words render at full size and only the rare long one scales
  // down to still fit on one line, with the section's height never moving.
  useEffect(() => {
    const line = titleLineRef.current;
    if (!line) return;

    const fit = () => {
      line.style.fontSize = "";
      const naturalWidth = line.scrollWidth;
      const available = line.clientWidth;
      if (naturalWidth > available && available > 0) {
        const base = parseFloat(getComputedStyle(line).fontSize);
        const scale = Math.max(available / naturalWidth, 0.62);
        line.style.fontSize = `${base * scale}px`;
      }
    };

    fit();
    const observer = new MutationObserver(fit);
    observer.observe(line, { characterData: true, childList: true, subtree: true });
    window.addEventListener("resize", fit);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, []);

  // Slow auto-advance between the two slides, paused while a visitor's mouse is over the
  // hero (matches the marquee's own hover-pause convention) so it never fights someone
  // trying to read or interact with a slide.
  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setActiveSlide((s) => (s + 1) % SLIDE_COUNT);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [paused]);

  const goTo = (index) => setActiveSlide((index + SLIDE_COUNT) % SLIDE_COUNT);

  const courseCount = stats ? formatStatCount(stats.total_courses) : "35+";

  return (
    <section
      className={`rbt-hero-v3 rbt-hero-v3--slide-${activeSlide}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="rbt-hero-v3__bg" aria-hidden="true">
        <span className="rbt-hero-v3__grid" />
        <span className="rbt-hero-v3__blob rbt-hero-v3__blob--a" />
        <span className="rbt-hero-v3__blob rbt-hero-v3__blob--b" />
      </div>

      <div className="rbt-hero-v3__container">
        {/* ── Slide 1: the code-editor moment (always mounted so Typed.js keeps running) ── */}
        {/* inert (not just aria-hidden) so the inactive slide's CTA links/buttons drop out of
            tab order too -- aria-hidden alone hides content from screen readers but doesn't
            stop keyboard focus from landing on a link that's invisible mid-crossfade. */}
        <div ref={(el) => (slideRefs.current[0] = el)} className={`rbt-hero-v3__slide ${activeSlide === 0 ? "is-active" : ""}`} aria-hidden={activeSlide !== 0}>
          <div className="rbt-hero-v3__copy">
            <span className="rbt-hero-v3__eyebrow">
              <span className="rbt-hero-v3__eyebrow-dot" />
              {t("hero.slide1.eyebrow", "Free forever · No credit card, ever")}
            </span>

            <h1 className="rbt-hero-v3__title">
              <span className="rbt-hero-v3__title-line" ref={titleLineRef}>
                {t("hero.slide1.titlePrefix", "Learn")} <span className="rbt-hero-v3__typed-wrap"><span ref={typedRef}></span></span>
              </span>
              <span className="rbt-hero-v3__title-accent">{t("hero.slide1.titleAccent", "100% free, forever.")}</span>
            </h1>

            <p className="rbt-hero-v3__desc">
              {t(
                "hero.slide1.desc",
                "From your first line of code to your first fully rendered 3D world, SmartTech Academy trades lectures for real, hands-on projects, in development, design, game creation, and everything in between, so you graduate with a portfolio, not just a certificate. Best part? All of it is free. Forever."
              )}
            </p>

            <div className="rbt-hero-v3__actions">
              <Link className="rbt-btn btn-gradient hover-icon-reverse" href={ROUTE.course}>
                <div className="icon-reverse-wrapper">
                  <span className="btn-text">{t("hero.slide1.ctaPrimary", "Start learning — it's free")}</span>
                  <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                  <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                </div>
              </Link>
              <Link className="rbt-hero-v3__secondary" href={ROUTE.course}>
                {t("hero.slide1.ctaSecondary", "Explore all courses")} <i className="feather-arrow-right"></i>
              </Link>
            </div>

            <div className="rbt-hero-v3__stats">
              <span className="rbt-hero-v3__stat">
                <strong>{courseCount}</strong> {t("hero.stats.courses", "Free courses")}
              </span>
              <span className="rbt-hero-v3__stat-divider" aria-hidden="true" />
              <span className="rbt-hero-v3__stat">
                <strong>{stats ? formatStatCount(stats.total_students) : "5+"}</strong> {t("hero.stats.students", "Students learning")}
              </span>
              <span className="rbt-hero-v3__stat-divider" aria-hidden="true" />
              <span className="rbt-hero-v3__stat">
                <strong>{stats?.total_categories ?? 8}</strong> {t("hero.stats.tracks", "Skill tracks")}
              </span>
            </div>
          </div>

          <div className="rbt-hero-v3__showcase">
            <span className="rbt-hero-v3__showcase-glow" aria-hidden="true" />

            <div className="rbt-hero-v3__showcase-stage">
              <div className="rbt-hero-v3__player">
                <div className="rbt-hero-v3__code-topbar">
                  <span className="rbt-hero-v3__code-dot rbt-hero-v3__code-dot--red" />
                  <span className="rbt-hero-v3__code-dot rbt-hero-v3__code-dot--yellow" />
                  <span className="rbt-hero-v3__code-dot rbt-hero-v3__code-dot--green" />
                  <span className="rbt-hero-v3__code-filename">career.js</span>
                </div>

                <div className="rbt-hero-v3__code-body">
                  <pre className="rbt-hero-v3__code">
<code><span className="rbt-hero-v3__tok-comment">{`// ${t("hero.code.comment", "your next 6 weeks, roughly")}`}</span>{"\n"}<span className="rbt-hero-v3__tok-kw">const</span> you = {"{"}{"\n"}  before: <span className="rbt-hero-v3__tok-str">&quot;{t("hero.code.before", "curious")}&quot;</span>,{"\n"}  after: <span className="rbt-hero-v3__tok-str">&quot;{t("hero.code.after", "job-ready")}&quot;</span>,{"\n"}{"}"};{"\n"}{"\n"}<span className="rbt-hero-v3__tok-kw">function</span> <span className="rbt-hero-v3__tok-fn">learn</span>(topic) {"{"}{"\n"}  <span className="rbt-hero-v3__tok-kw">return</span> build(topic){"\n"}    .ship(){"\n"}    .repeat();{"\n"}{"}"}{"\n"}{"\n"}learn(<span className="rbt-hero-v3__tok-str">&quot;{t("hero.code.topic", "web development")}&quot;</span>);<span className="rbt-hero-v3__code-cursor">&#8203;</span></code>
                  </pre>
                </div>

                <div className="rbt-hero-v3__code-footer">
                  <span className="rbt-hero-v3__code-badge">
                    <span className="rbt-hero-v3__code-badge-dot" />
                    {t("hero.code.liveBadge", "Live in the classroom")}
                  </span>
                  <span className="rbt-hero-v3__code-run">
                    <i className="feather-play"></i> {t("hero.code.run", "Run")}
                  </span>
                </div>
              </div>

              <div className="rbt-hero-v3__float rbt-hero-v3__float--certificate">
                <span className="rbt-hero-v3__float-icon"><i className="feather-check"></i></span>
                <div className="rbt-hero-v3__float-text">
                  <strong>{t("hero.float.certReadyTitle", "Certificate ready")}</strong>
                  <span>{t("hero.float.certReadyDesc", "Shareable on LinkedIn")}</span>
                </div>
              </div>

              <div className="rbt-hero-v3__float rbt-hero-v3__float--community">
                <div className="rbt-hero-v3__avatars" aria-hidden="true">
                  <span>A</span>
                  <span>M</span>
                  <span>J</span>
                  <span className="rbt-hero-v3__avatars-more">+</span>
                </div>
                <div className="rbt-hero-v3__float-text">
                  <strong>{stats ? formatStatCount(stats.total_students) : "5,000+"}</strong>
                  <span>{t("hero.float.learnersThisMonth", "learners this month")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Slide 2: outcomes / portfolio moment ── */}
        <div ref={(el) => (slideRefs.current[1] = el)} className={`rbt-hero-v3__slide ${activeSlide === 1 ? "is-active" : ""}`} aria-hidden={activeSlide !== 1}>
          <div className="rbt-hero-v3__copy">
            <span className="rbt-hero-v3__eyebrow rbt-hero-v3__eyebrow--alt">
              <span className="rbt-hero-v3__eyebrow-dot" />
              {t("hero.slide2.eyebrow", "Loved by {{count}} learners", {
                count: stats ? formatStatCount(stats.total_students) : t("hero.slide2.thousandsOf", "thousands of"),
              })}
            </span>

            <h1 className="rbt-hero-v3__title">
              <span className="rbt-hero-v3__title-line">{t("hero.slide2.titleLine", "Build a portfolio.")}</span>
              <span className="rbt-hero-v3__title-accent rbt-hero-v3__title-accent--alt">
                {t("hero.slide2.titleAccent", "Not just a certificate.")}
              </span>
            </h1>

            <p className="rbt-hero-v3__desc">
              {t(
                "hero.slide2.desc",
                "Every course ships a real shippable project, not a quiz score. Employers care about what you've actually built, so that's exactly what you'll walk away with, alongside a certificate to back it up."
              )}
            </p>

            <div className="rbt-hero-v3__actions">
              <Link className="rbt-btn btn-gradient hover-icon-reverse" href={ROUTE.course}>
                <div className="icon-reverse-wrapper">
                  <span className="btn-text">{t("hero.slide2.ctaPrimary", "See what you'll build")}</span>
                  <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                  <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                </div>
              </Link>
              <Link className="rbt-hero-v3__secondary" href={ROUTE.course}>
                {t("hero.slide2.ctaSecondary", "Browse the catalog")} <i className="feather-arrow-right"></i>
              </Link>
            </div>

            <div className="rbt-hero-v3__stats">
              <span className="rbt-hero-v3__stat">
                <strong>100%</strong> {t("hero.slide2.statProjectBased", "Project-based")}
              </span>
              <span className="rbt-hero-v3__stat-divider" aria-hidden="true" />
              <span className="rbt-hero-v3__stat">
                <strong>{stats ? formatStatCount(stats.total_instructors) : "12+"}</strong> {t("hero.slide2.statInstructors", "Real instructors")}
              </span>
              <span className="rbt-hero-v3__stat-divider" aria-hidden="true" />
              <span className="rbt-hero-v3__stat">
                <strong>$0</strong> {t("hero.slide2.statEver", "Ever")}
              </span>
            </div>
          </div>

          <div className="rbt-hero-v3__showcase">
            <span className="rbt-hero-v3__showcase-glow rbt-hero-v3__showcase-glow--alt" aria-hidden="true" />

            <div className="rbt-hero-v3__showcase-stage">
              <div className="rbt-hero-v3__cert">
                <div className="rbt-hero-v3__cert-top">
                  <span className="rbt-hero-v3__cert-seal">
                    <i className="feather-award"></i>
                  </span>
                  <span className="rbt-hero-v3__cert-kicker">{t("hero.cert.kicker", "Course Certificate")}</span>
                </div>

                <strong className="rbt-hero-v3__cert-name">Ada Chen</strong>
                <span className="rbt-hero-v3__cert-course">{t("hero.cert.courseName", "Full-Stack Development with Laravel")}</span>

                <div className="rbt-hero-v3__cert-stats">
                  <div>
                    <strong>12</strong>
                    <span>{t("hero.cert.statProjects", "Projects shipped")}</span>
                  </div>
                  <div>
                    <strong>38</strong>
                    <span>{t("hero.cert.statSkills", "Skills unlocked")}</span>
                  </div>
                  <div>
                    <strong>96h</strong>
                    <span>{t("hero.cert.statHours", "Hours learned")}</span>
                  </div>
                </div>
              </div>

              <div className="rbt-hero-v3__float rbt-hero-v3__float--certificate">
                <span className="rbt-hero-v3__float-icon"><i className="feather-github"></i></span>
                <div className="rbt-hero-v3__float-text">
                  <strong>{t("hero.float.portfolioReadyTitle", "Portfolio-ready")}</strong>
                  <span>{t("hero.float.portfolioReadyDesc", "Real code, real repos")}</span>
                </div>
              </div>

              <div className="rbt-hero-v3__float rbt-hero-v3__float--community">
                <div className="rbt-hero-v3__avatars" aria-hidden="true">
                  <span>A</span>
                  <span>M</span>
                  <span>J</span>
                  <span className="rbt-hero-v3__avatars-more">+</span>
                </div>
                <div className="rbt-hero-v3__float-text">
                  <strong>{stats ? formatStatCount(stats.total_students) : "5,000+"}</strong>
                  <span>{t("hero.float.learnersThisMonth", "learners this month")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="rbt-hero-v3__arrow rbt-hero-v3__arrow--prev"
        aria-label={t("hero.nav.prevSlide", "Previous slide")}
        onClick={() => goTo(activeSlide - 1)}
      >
        <i className="feather-chevron-left"></i>
      </button>
      <button
        type="button"
        className="rbt-hero-v3__arrow rbt-hero-v3__arrow--next"
        aria-label={t("hero.nav.nextSlide", "Next slide")}
        onClick={() => goTo(activeSlide + 1)}
      >
        <i className="feather-chevron-right"></i>
      </button>

      <div className="rbt-hero-v3__dots" role="tablist" aria-label={t("hero.nav.heroSlides", "Hero slides")}>
        {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
          <button
            type="button"
            key={i}
            className={i === activeSlide ? "is-active" : ""}
            role="tab"
            aria-selected={i === activeSlide}
            aria-label={t("hero.nav.showSlide", "Show slide {{number}}", { number: i + 1 })}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </section>
  );
};
